import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Search, MoreVertical, Package, UserPlus, UserCog, Trash, Edit, CheckCircle, Plus, Loader2, Mail, User, Phone, Lock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/lib/supabase";
import toast from "react-hot-toast";
import LoadingSpinner from "@/components/LoadingSpinner";
import { authService } from "@/lib/auth/authService";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

// Esquema de validación para el formulario de registro
const registerSchema = z
  .object({
    name: z.string().min(1, 'El nombre es obligatorio'),
    email: z.string().email('Introduce un correo electrónico válido').min(1, 'El correo electrónico es obligatorio'),
    phone: z.string().optional(),
    password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres'),
    confirmPassword: z.string().min(6, 'La confirmación de contraseña es obligatoria'),
    company: z.string().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  });

// Tipo del formulario de registro
type RegisterFormData = z.infer<typeof registerSchema>;

// Tipos de datos
interface User {
  id: string;
  email: string;
  created_at: string;
  user_metadata: {
    name?: string;
    phone?: string;
    company?: string;
  };
}

interface Machine {
  id: number;
  name: string;
  price: number;
  category?: string;
  imageUrl?: string;
  purchaseDate?: string;
}

interface UserMachine {
  id: number;
  userId: string;
  machineId: number;
  purchaseDate: string;
  machineName: string;
  machinePrice: number;
  machineImageUrl?: string;
}

// Datos de ejemplo para máquinas
const mockMachines: Machine[] = [
  {
    id: 101,
    name: "Embutidora Industrial E-200",
    price: 15950,
    category: "Embutidoras",
    imageUrl: "/img/machines/embutidora-e200.jpg"
  },
  {
    id: 102,
    name: "Cortadora de Precisión C-3000",
    price: 8750,
    category: "Cortadoras",
    imageUrl: "/img/machines/cortadora-c3000.jpg"
  },
  {
    id: 103,
    name: "Mezcladora Industrial MIX-500",
    price: 12300,
    category: "Mezcladoras",
    imageUrl: "/img/machines/mezcladora-mix500.jpg"
  },
  {
    id: 104,
    name: "Envasadora al Vacío V-1000",
    price: 9800,
    category: "Envasadoras",
    imageUrl: "/img/machines/envasadora-v1000.jpg"
  },
  {
    id: 105,
    name: "Picadora de Carne P-750",
    price: 6200,
    category: "Picadoras",
    imageUrl: "/img/machines/picadora-p750.jpg"
  }
];

// Función para obtener las máquinas disponibles (catálogo)
const fetchMachines = async (): Promise<Machine[]> => {
  try {
    // Obtener productos de la tabla products
    const { data, error } = await supabase
      .from('products')
      .select('id, name, price, category_id, images')
      .order('id', { ascending: true });

    if (!error && data && data.length > 0) {
      console.log("Productos obtenidos del catálogo:", data.length);
      return data.map((product: any) => ({
        id: product.id,
        name: product.name,
        // Convertir price de texto a número
        price: parseFloat(product.price.replace(/[^\d.,]/g, '').replace(',', '.')) || 0,
        category: product.category_id?.toString() || 'General',
        // Tomar la primera imagen del array de imágenes si existe
        imageUrl: product.images && product.images.length > 0 ? product.images[0] : undefined
      }));
    }

    console.log("No se encontraron productos, usando datos de ejemplo");
    return mockMachines;
  } catch (error) {
    console.error("Error en la función fetchMachines:", error);
    return mockMachines;
  }
};

// Función para obtener los usuarios de Supabase
const fetchUsers = async (): Promise<User[]> => {
  try {
    // Intentar obtener usuarios de profiles
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (!error && data && data.length > 0) {
        console.log("Usuarios obtenidos de profiles:", data.length);
        return data.map(profile => ({
          id: profile.id,
          email: profile.email || '',
          created_at: profile.created_at || new Date().toISOString(),
          user_metadata: {
            name: profile.name || '',
            phone: profile.phone || '',
            company: profile.company || ''
          }
        }));
      }
    } catch (profilesError) {
      console.error("Error al acceder a la tabla profiles:", profilesError);
    }

    // Intentar obtener el usuario actual como último recurso
    try {
      const { data: { user }, error } = await supabase.auth.getUser();

      if (!error && user) {
        console.log("Usando el usuario actual como ejemplo:", user.email);
        // Devolvemos solo el usuario autenticado como ejemplo
        return [{
          id: user.id,
          email: user.email || '',
          created_at: user.created_at || new Date().toISOString(),
          user_metadata: user.user_metadata || {}
        }];
      }
    } catch (userError) {
      console.error("Error al obtener el usuario actual:", userError);
    }

    // Si no hay usuarios reales, mostrar un mensaje y devolver los usuarios de demostración
    console.warn("No se pudieron obtener usuarios reales, mostrando ejemplos para desarrollo");

    return [
      {
        id: "demo-1",
        email: "ejemplo1@innotec.es",
        created_at: new Date().toISOString(),
        user_metadata: {
          name: "Usuario Ejemplo 1",
          phone: "600123456",
          company: "Innotec Ejemplo"
        }
      }
    ];
  } catch (error: any) {
    console.error("Error general en fetchUsers:", error.message);
    return [];
  }
};

// Función para obtener las máquinas asignadas a usuarios
const fetchUserMachines = async (): Promise<UserMachine[]> => {
  try {
    // Verificar si existe la tabla user_machines
    try {
      const { data, error } = await supabase
        .from('user_machines')
        .select('*')
        .limit(1);

      // Si la tabla existe, intentar obtener todos los datos
      if (!error) {
        const { data: allData, error: allDataError } = await supabase
          .from('user_machines')
          .select('*')
          .order('id', { ascending: false });

        if (!allDataError && allData && allData.length > 0) {
          console.log("Máquinas obtenidas de la base de datos:", allData.length);
          return allData.map((item: any) => ({
            id: item.id,
            userId: item.user_id,
            machineId: item.machine_id,
            purchaseDate: item.purchase_date,
            machineName: item.machine_name,
            machinePrice: item.machine_price,
            machineImageUrl: item.machine_image_url
          }));
        }
      }
    } catch (tableError) {
      console.error("Error al verificar tabla user_machines:", tableError);
    }

    // Si no se pudo obtener datos reales, crear la tabla
    console.warn("La tabla user_machines no existe. Recomendación: crear la tabla con SQL.");
    console.log("Ejecuta este SQL en Supabase: CREATE TABLE public.user_machines (id SERIAL PRIMARY KEY, user_id UUID NOT NULL, machine_id INTEGER NOT NULL, purchase_date TIMESTAMP WITH TIME ZONE, machine_name TEXT NOT NULL, machine_price NUMERIC(10,2), machine_image_url TEXT)");

    // Devolver datos de ejemplo mínimos
    return [
      {
        id: 999,
        userId: "tabla-no-existe",
        machineId: 100,
        purchaseDate: new Date().toISOString(),
        machineName: "Ejemplo - Tabla no existe",
        machinePrice: 0,
        machineImageUrl: ""
      }
    ];
  } catch (error) {
    console.error("Error en la función fetchUserMachines:", error);
    return [];
  }
};

export function UsersTab() {
  // Estados
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showAddMachineDialog, setShowAddMachineDialog] = useState(false);
  const [selectedMachineId, setSelectedMachineId] = useState<number | null>(null);
  const [purchaseDate, setPurchaseDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [machines, setMachines] = useState<Machine[]>(mockMachines);

  // Nuevo estado para el modal de registro de usuarios
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  // Inicializar useForm para el formulario de registro
  const {
    register: registerUser,
    handleSubmit: handleRegisterSubmit,
    formState: { errors: registerErrors },
    reset: resetRegisterForm,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      company: '',
    },
  });

  // Efecto para verificar token de Supabase (colocado aquí para mantener el orden de los hooks)
  useEffect(() => {
    // Verificar si tenemos un token de Supabase disponible
    const supabaseToken = localStorage.getItem("supabase.auth.token");

    if (supabaseToken) {
      console.log("Token de Supabase disponible, utilizándolo para las consultas");
      // Si se necesita hacer algo especial con el token, podríamos hacerlo aquí
    }
  }, []);

  // Consultas con React Query
  const { data: users = [], isLoading: isLoadingUsers, error: usersError, refetch: refetchUsers } = useQuery({
    queryKey: ['users'],
    queryFn: fetchUsers,
  });

  const {
    data: userMachines = [],
    isLoading: isLoadingMachines,
    error: machinesError,
    refetch: refetchUserMachines
  } = useQuery({
    queryKey: ['userMachines'],
    queryFn: fetchUserMachines,
  });

  const {
    data: availableMachines = mockMachines,
    isLoading: isLoadingCatalog
  } = useQuery({
    queryKey: ['machinesCatalog'],
    queryFn: fetchMachines,
  });

  // Actualizar el estado de machines cuando se carguen los datos del catálogo
  useEffect(() => {
    if (availableMachines && availableMachines.length > 0) {
      setMachines(availableMachines);
    }
  }, [availableMachines]);

  // Filtrar usuarios por búsqueda
  const filteredUsers = users.filter(user => {
    const query = searchQuery.toLowerCase();
    const name = user.user_metadata?.name?.toLowerCase() || "";
    const email = user.email.toLowerCase();
    const company = user.user_metadata?.company?.toLowerCase() || "";

    return name.includes(query) || email.includes(query) || company.includes(query);
  });

  // Obtener máquinas de un usuario
  const getUserMachines = (userId: string) => {
    return userMachines.filter(um => um.userId === userId);
  };

  // Funciones de gestión
  const handleViewUserDetails = (user: User) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const handleAddMachine = async () => {
    if (!selectedUser || !selectedMachineId) return;

    const machineToAdd = machines.find(m => m.id === selectedMachineId);
    if (!machineToAdd) return;

    // Mostrar estado de carga
    const loadingToast = toast.loading('Asignando máquina...');

    try {
      // Datos para insertar en la tabla
      const machineData = {
        user_id: selectedUser.id,
        machine_id: machineToAdd.id,
        purchase_date: new Date(purchaseDate).toISOString(),
        machine_name: machineToAdd.name,
        machine_price: machineToAdd.price,
        machine_image_url: machineToAdd.imageUrl || null
      };

      console.log('Datos a insertar:', machineData);

      // Insertar en la tabla user_machines
      const { data, error } = await supabase
        .from('user_machines')
        .insert(machineData)
        .select();

      if (error) {
        console.error("Error al asignar máquina:", error);
        toast.error(`Error: ${error.message}`);
        throw error;
      }

      console.log('Máquina asignada correctamente:', data);

      // Recargar las máquinas de usuarios
      refetchUserMachines();

      // Mostrar mensaje de éxito
      toast.success(`Máquina "${machineToAdd.name}" asignada correctamente a ${selectedUser.user_metadata?.name || selectedUser.email}`);
    } catch (error: any) {
      console.error("Error en la función handleAddMachine:", error);
      toast.error(`No se pudo asignar la máquina: ${error.message || 'Error desconocido'}`);
    } finally {
      // Cerrar el toast de carga
      toast.dismiss(loadingToast);

      // Cerrar el diálogo y resetear los valores
      setShowAddMachineDialog(false);
      setSelectedMachineId(null);
      setPurchaseDate(new Date().toISOString().split('T')[0]);
    }
  };

  const handleRemoveMachine = async (userMachineId: number) => {
    const loadingToast = toast.loading('Eliminando asignación...');

    try {
      console.log('Eliminando asignación de máquina ID:', userMachineId);

      // Eliminar de la tabla user_machines
      const { error } = await supabase
        .from('user_machines')
        .delete()
        .eq('id', userMachineId);

      if (error) {
        console.error("Error al eliminar máquina:", error);
        toast.error(`Error: ${error.message}`);
        throw error;
      }

      // Recargar las máquinas de usuarios
      refetchUserMachines();

      // Mostrar mensaje de éxito
      toast.success("Máquina eliminada correctamente");
    } catch (error: any) {
      console.error("Error en la función handleRemoveMachine:", error);
      toast.error(`No se pudo eliminar la máquina: ${error.message || 'Error desconocido'}`);
    } finally {
      // Cerrar el toast de carga
      toast.dismiss(loadingToast);
    }
  };

  // Formatear fecha para mostrar
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  // Formatear precio para mostrar
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  // Función para manejar el registro de usuarios
  const onRegisterSubmit = async (data: RegisterFormData) => {
    setIsRegistering(true);

    try {
      // Extraer lo que necesitamos para registrar al usuario
      const { confirmPassword, ...registerData } = data;

      const result = await authService.signUpWithSupabaseClient(registerData);

      if (result.success) {
        // Si el registro es exitoso y tenemos el ID del usuario, actualizar el campo company
        if (result.data?.user?.id && data.company) {
          try {
            const { error: profileError } = await supabase
              .from("profiles")
              .update({ company: data.company })
              .eq('id', result.data.user.id);

            if (profileError) {
              console.warn('Error al actualizar el campo empresa:', profileError);
            }
          } catch (error) {
            console.error('Error al actualizar el perfil con el campo empresa:', error);
          }
        }

        toast.success('Usuario registrado correctamente. Se ha enviado un correo electrónico de confirmación.');

        // Si el registro es exitoso, actualizar la lista de usuarios
        // y cerrar el modal
        resetRegisterForm();
        setShowRegisterDialog(false);

        // Recargar la lista de usuarios después de un breve retraso
        setTimeout(() => {
          refetchUsers();
        }, 2000);
      } else {
        toast.error(result.message || 'No se pudo completar el registro. Inténtalo de nuevo.');
      }
    } catch (error: any) {
      console.error('Error en el proceso de registro:', error);
      toast.error('Ocurrió un error inesperado. Inténtalo de nuevo más tarde.');
    } finally {
      setIsRegistering(false);
    }
  };

  // Mostrar pantalla de carga mientras se obtienen los datos
  if (isLoadingUsers || isLoadingMachines || isLoadingCatalog) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px]">
        <LoadingSpinner />
        <p className="mt-4 text-zinc-400">Cargando datos de usuarios y máquinas...</p>
      </div>
    );
  }

  // Mostrar mensaje de error si falla alguna consulta
  if (usersError || machinesError) {
    return (
      <div className="text-center py-8 text-red-400">
        <p className="text-lg font-medium mb-2">Error al cargar los datos</p>
        <p className="text-sm text-zinc-400">
          Por favor, intenta recargar la página o contacta con soporte técnico
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Gestión de Usuarios</h2>
        <Button
          className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black"
          onClick={() => setShowRegisterDialog(true)}
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Añadir Usuario
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-zinc-500" />
        <Input
          placeholder="Buscar por nombre, email o empresa..."
          className="pl-9 bg-zinc-800/30 border-zinc-700/50"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="rounded-md bg-zinc-900/50 border border-zinc-800">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-zinc-800/50 border-zinc-800">
              <TableHead className="text-zinc-400">Usuario</TableHead>
              <TableHead className="text-zinc-400">Email</TableHead>
              <TableHead className="text-zinc-400">Empresa</TableHead>
              <TableHead className="text-zinc-400">Fecha de registro</TableHead>
              <TableHead className="text-zinc-400">Máquinas</TableHead>
              <TableHead className="text-zinc-400 w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-zinc-500">
                  No se encontraron usuarios con los criterios de búsqueda
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => {
                const userMachineCount = getUserMachines(user.id).length;
                return (
                  <TableRow key={user.id} className="hover:bg-zinc-800/50 border-zinc-800">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8 bg-zinc-700">
                          <AvatarFallback className="bg-[#FCAA0D]/20 text-[#FCAA0D]">
                            {user.user_metadata?.name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="font-medium text-white">{user.user_metadata?.name || 'Usuario sin nombre'}</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-zinc-400">{user.email}</TableCell>
                    <TableCell className="text-zinc-400">{user.user_metadata?.company || '-'}</TableCell>
                    <TableCell className="text-zinc-400">{formatDate(user.created_at)}</TableCell>
                    <TableCell>
                      {userMachineCount > 0 ? (
                        <Badge variant="outline" className="bg-green-900/20 text-green-400 border-green-800">
                          {userMachineCount} máquina{userMachineCount !== 1 ? 's' : ''}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-zinc-800/50 text-zinc-500 border-zinc-700">
                          Sin máquinas
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-zinc-800 border-zinc-700">
                          <DropdownMenuItem
                            className="cursor-pointer text-white hover:bg-zinc-700"
                            onClick={() => handleViewUserDetails(user)}
                          >
                            <UserCog className="h-4 w-4 mr-2" />
                            Gestionar
                          </DropdownMenuItem>
                          <DropdownMenuItem className="cursor-pointer text-white hover:bg-zinc-700">
                            <Edit className="h-4 w-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem className="cursor-pointer text-red-400 hover:bg-red-900/20">
                            <Trash className="h-4 w-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Modal de detalles de usuario */}
      {selectedUser && (
        <Dialog open={showUserDetails} onOpenChange={setShowUserDetails}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-4xl">
            <DialogHeader>
              <DialogTitle className="text-2xl flex items-center gap-3">
                <Avatar className="h-10 w-10 bg-zinc-700">
                  <AvatarFallback className="bg-[#FCAA0D]/20 text-[#FCAA0D]">
                    {selectedUser.user_metadata?.name?.charAt(0) || selectedUser.email.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                {selectedUser.user_metadata?.name || 'Usuario sin nombre'}
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                Gestión de datos y máquinas asignadas al usuario
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4">
              <Tabs defaultValue="info">
                <TabsList className="bg-zinc-800">
                  <TabsTrigger value="info" className="data-[state=active]:bg-[#FCAA0D] data-[state=active]:text-black">
                    Información
                  </TabsTrigger>
                  <TabsTrigger value="machines" className="data-[state=active]:bg-[#FCAA0D] data-[state=active]:text-black">
                    Máquinas Asignadas
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="info" className="mt-4">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Card className="bg-zinc-800/50 border-zinc-700">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium text-zinc-400">Email</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-white">{selectedUser.email}</p>
                        </CardContent>
                      </Card>

                      <Card className="bg-zinc-800/50 border-zinc-700">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium text-zinc-400">Teléfono</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-white">{selectedUser.user_metadata?.phone || 'No disponible'}</p>
                        </CardContent>
                      </Card>

                      <Card className="bg-zinc-800/50 border-zinc-700">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium text-zinc-400">Empresa</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-white">{selectedUser.user_metadata?.company || 'No disponible'}</p>
                        </CardContent>
                      </Card>

                      <Card className="bg-zinc-800/50 border-zinc-700">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium text-zinc-400">Fecha de registro</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-white">{formatDate(selectedUser.created_at)}</p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="machines" className="mt-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-white">Máquinas del usuario</h3>
                    <Button
                      onClick={() => setShowAddMachineDialog(true)}
                      className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Asignar máquina
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {getUserMachines(selectedUser.id).length === 0 ? (
                      <div className="text-center py-8 border border-zinc-800 rounded-md bg-zinc-800/30">
                        <Package className="h-12 w-12 mx-auto text-zinc-700 mb-2" />
                        <p className="text-zinc-500">Este usuario no tiene máquinas asignadas</p>
                        <Button
                          onClick={() => setShowAddMachineDialog(true)}
                          variant="outline"
                          className="mt-4 border-zinc-700 hover:bg-zinc-800"
                        >
                          Asignar máquina
                        </Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4">
                        {getUserMachines(selectedUser.id).map((userMachine) => (
                          <Card key={userMachine.id} className="bg-zinc-800/30 border-zinc-700 hover:bg-zinc-800/50 transition-colors">
                            <div className="p-4 flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                {userMachine.machineImageUrl ? (
                                  <div className="h-12 w-12 rounded-md overflow-hidden flex-shrink-0">
                                    <img
                                      src={userMachine.machineImageUrl}
                                      alt={userMachine.machineName}
                                      className="h-full w-full object-cover"
                                      onError={(e) => {
                                        // Si falla la carga de la imagen, mostrar un icono
                                        e.currentTarget.style.display = 'none';
                                        e.currentTarget.parentElement?.classList.add('bg-zinc-700');
                                        e.currentTarget.parentElement?.classList.add('flex');
                                        e.currentTarget.parentElement?.classList.add('items-center');
                                        e.currentTarget.parentElement?.classList.add('justify-center');
                                        const icon = document.createElement('div');
                                        icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-[#FCAA0D]"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.29 7 12 12 20.71 7"></polyline><line x1="12" y1="22" x2="12" y2="12"></line></svg>';
                                        e.currentTarget.parentElement?.appendChild(icon);
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <div className="h-12 w-12 bg-zinc-700 rounded-md flex items-center justify-center flex-shrink-0">
                                    <Package className="h-6 w-6 text-[#FCAA0D]" />
                                  </div>
                                )}
                                <div>
                                  <h4 className="font-medium text-white">{userMachine.machineName}</h4>
                                  <div className="flex flex-wrap items-center gap-4 mt-1">
                                    <p className="text-sm text-zinc-400">
                                      ID: {userMachine.machineId}
                                    </p>
                                    <p className="text-sm text-zinc-400">
                                      Fecha de compra: {formatDate(userMachine.purchaseDate)}
                                    </p>
                                    <p className="text-sm text-green-400 font-medium">
                                      Precio: {formatPrice(userMachine.machinePrice)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-400 hover:bg-red-900/20 hover:text-red-300"
                                onClick={() => handleRemoveMachine(userMachine.id)}
                              >
                                <Trash className="h-4 w-4" />
                              </Button>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal para asignar nueva máquina */}
      <Dialog open={showAddMachineDialog} onOpenChange={setShowAddMachineDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>Asignar máquina a usuario</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Selecciona una máquina para asignar a {selectedUser?.user_metadata?.name || selectedUser?.email}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Máquina</label>
              <Select onValueChange={(value) => setSelectedMachineId(Number(value))}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Selecciona una máquina" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {machines.map((machine) => (
                    <SelectItem key={machine.id} value={machine.id.toString()} className="text-white hover:bg-zinc-700">
                      {machine.name} - {formatPrice(machine.price)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Fecha de compra</label>
              <Input
                type="date"
                className="bg-zinc-800 border-zinc-700"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setShowAddMachineDialog(false)}
              className="border-zinc-700 hover:bg-zinc-800"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAddMachine}
              className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black"
              disabled={!selectedMachineId}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Asignar máquina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal para registrar nuevo usuario */}
      <Dialog open={showRegisterDialog} onOpenChange={setShowRegisterDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Registrar nuevo usuario</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Crea una nueva cuenta de usuario. Se enviará un correo de confirmación.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleRegisterSubmit(onRegisterSubmit)} className="space-y-4 mt-4">
            <div className="space-y-1">
              <label htmlFor="name" className="text-white/90 flex items-center gap-2 text-sm font-medium">
                <User className="w-4 h-4 text-[#FCAA0D]" />
                Nombre completo
              </label>
              <Input
                id="name"
                type="text"
                className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]/50 focus:border-[#FCAA0D]"
                placeholder="Nombre y apellidos"
                {...registerUser('name')}
              />
              {registerErrors.name && <p className="text-red-500 text-xs mt-1">{registerErrors.name.message}</p>}
            </div>

            <div className="space-y-1">
              <label htmlFor="email" className="text-white/90 flex items-center gap-2 text-sm font-medium">
                <Mail className="w-4 h-4 text-[#FCAA0D]" />
                Correo electrónico
              </label>
              <Input
                id="email"
                type="email"
                className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]/50 focus:border-[#FCAA0D]"
                placeholder="ejemplo@correo.com"
                {...registerUser('email')}
              />
              {registerErrors.email && <p className="text-red-500 text-xs mt-1">{registerErrors.email.message}</p>}
            </div>

            <div className="space-y-1">
              <label htmlFor="phone" className="text-white/90 flex items-center gap-2 text-sm font-medium">
                <Phone className="w-4 h-4 text-[#FCAA0D]" />
                Teléfono (opcional)
              </label>
              <Input
                id="phone"
                type="tel"
                className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]/50 focus:border-[#FCAA0D]"
                placeholder="+34 600 000 000"
                {...registerUser('phone')}
              />
              {registerErrors.phone && <p className="text-red-500 text-xs mt-1">{registerErrors.phone.message}</p>}
            </div>

            <div className="space-y-1">
              <label htmlFor="company" className="text-white/90 flex items-center gap-2 text-sm font-medium">
                <Package className="w-4 h-4 text-[#FCAA0D]" />
                Empresa (opcional)
              </label>
              <Input
                id="company"
                type="text"
                className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]/50 focus:border-[#FCAA0D]"
                placeholder="Nombre de la empresa"
                {...registerUser('company')}
              />
              {registerErrors.company && <p className="text-red-500 text-xs mt-1">{registerErrors.company.message}</p>}
            </div>

            <div className="space-y-1">
              <label htmlFor="password" className="text-white/90 flex items-center gap-2 text-sm font-medium">
                <Lock className="w-4 h-4 text-[#FCAA0D]" />
                Contraseña
              </label>
              <Input
                id="password"
                type="password"
                className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]/50 focus:border-[#FCAA0D]"
                placeholder="******"
                {...registerUser('password')}
              />
              {registerErrors.password && <p className="text-red-500 text-xs mt-1">{registerErrors.password.message}</p>}
            </div>

            <div className="space-y-1">
              <label htmlFor="confirmPassword" className="text-white/90 flex items-center gap-2 text-sm font-medium">
                <Lock className="w-4 h-4 text-[#FCAA0D]" />
                Confirmar contraseña
              </label>
              <Input
                id="confirmPassword"
                type="password"
                className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]/50 focus:border-[#FCAA0D]"
                placeholder="******"
                {...registerUser('confirmPassword')}
              />
              {registerErrors.confirmPassword && (
                <p className="text-red-500 text-xs mt-1">{registerErrors.confirmPassword.message}</p>
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowRegisterDialog(false)}
                className="border-zinc-700 hover:bg-zinc-800"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isRegistering}
                className="bg-[#FCAA0D] text-zinc-900 font-bold px-6 py-3 rounded-xl hover:bg-[#FCAA0D]/90 transition-colors flex items-center justify-center gap-2"
              >
                {isRegistering ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creando usuario...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" />
                    Crear usuario
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
